import { cn } from '@/app/common/utilities'
import axiosInstance from '@/libs/axios_config'
import { Dialog, DialogContent } from '@/shad/ui/dialog'
import { useEffect, useState } from 'react'
import { EllipsesLoader } from '@/common/components/EllipsesLoader.tsx'

interface IImage {
  path?: string
  src?: string
  alt?: string
  width?: number | string
  allowZoom?: boolean
  className?: string
  onFileReady?: (file: File) => void
  isPdf?: boolean
}

const Image: React.FC<IImage> = ({
  path,
  src,
  alt,
  width,
  allowZoom = false,
  className,
  onFileReady,
  isPdf = false,
}) => {
  const [fileUrl, setFileUrl] = useState<string>()
  const [isZoomed, setIsZoomed] = useState(false)
  const [fileType, setFileType] = useState<'image' | 'pdf'>(isPdf ? 'pdf' : 'image')
  const [isLoading, setIsLoading] = useState(false)

  const blobToFile = (blob: Blob, fileName: string): File => {
    // Create a File from the Blob
    const file = new File([blob], fileName, {
      type: blob.type,
      lastModified: new Date().getTime(),
    })
    return file
  }

  const fetchFile = async (path: string) => {
    if (path) {
      try {
        setIsLoading(true)
        const response = await axiosInstance.get(
          `/temp-public-download-file?path=${path}`,
          {
            responseType: 'blob',
          }
        )

        // Get the file name from the path or create a default one
        const fileName = path.split('/').pop() || 'file'

        // Determine file type based on response or extension
        const isPdfFile = response.data.type === 'application/pdf' ||
          path.toLowerCase().endsWith('.pdf')

        setFileType(isPdfFile ? 'pdf' : 'image')

        // Convert blob to File
        const file = blobToFile(response.data, fileName)

        // Notify parent component if callback is provided
        if (onFileReady) {
          onFileReady(file)
        }

        // Create URL for displaying the file
        setFileUrl(URL.createObjectURL(response.data))
      } catch (error) {
        setFileUrl(undefined)
      } finally {
        setIsLoading(false)
      }
    }
  }

  useEffect(() => {
    if (path && !fileUrl) {
      if (path) {
        fetchFile(path)
      }
    } else if (src) {
      // If src is directly provided, check if it's a PDF
      const isPdfFile = isPdf || (typeof src === 'string' && src.toLowerCase().endsWith('.pdf'))
      setFileType(isPdfFile ? 'pdf' : 'image')
    }
  }, [path, src])

  // Cleanup URL object when component unmounts
  useEffect(() => {
    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl)
      }
    }
  }, [fileUrl])

  if (!fileUrl && !src) {
    return <></>
  }

  const fileSource = src ?? fileUrl

  const handleZoomImage = () => {
      if (path) {
        fetchFile(path)
        setIsZoomed(true)
      }
  }

  return (
    <>
      <div
        className="relative inline-block cursor-pointer"
        onClick={handleZoomImage}
      >
        {fileType === 'image' ? (
          <img
            src={fileSource}
            alt={alt}
            width={width}
            className={cn('max-w-full h-auto', className)}
          />
        ) : (
          <div className={cn('relative', className)}>
            <object
              data={fileSource}
              type="application/pdf"
              width={width || "100%"}
              height="400px"
              className="border border-gray-200 rounded"
            >
              <div className="flex items-center justify-center h-full w-full bg-gray-100 rounded p-4">
                <p>Unable to display PDF. <a href={fileSource} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Download PDF</a></p>
              </div>
            </object>
          </div>
        )}

        {allowZoom && (
          <button
            onClick={() => setIsZoomed(true)}
            className="absolute top-2 right-2 py-1 px-2 bg-white hover:bg-white/90 rounded-full text-xs transition-colors"
            aria-label="Zoom file"
          >
            Zoom
          </button>
        )}
      </div>
      <Dialog open={isZoomed} onOpenChange={setIsZoomed}>
        <DialogContent className="max-w-screen-lg w-full p-1">
          {isLoading ? (
            <div className="flex justify-center items-center h-[300px]">
              <EllipsesLoader size="md" />
            </div>
          ) : fileType === 'image' ? (
            <img
              src={fileSource}
              alt={alt}
              className={cn('w-full h-auto object-contain')}
            />
          ) : (
            <object
              data={fileSource}
              type="application/pdf"
              className="w-full h-[calc(100vh-100px)]"
            >
              <div className="flex items-center justify-center h-full w-full bg-gray-100 rounded p-4">
                <p>
                  Unable to display PDF.{' '}
                  <a
                    href={fileSource}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    Download PDF
                  </a>
                </p>
              </div>
            </object>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default Image
